import IconArrowLeft from "@shared/components/icons/ArrowLeft"
import { ExpandChevron } from "@shared/components/menus/ExpandChevron"
import { Dropdown } from "@shared/components/untitledui/base/dropdown/dropdown"
import { cx } from "@shared/components/untitledui/util/cx"
import { tracked } from "@supergrain/kernel/react"
import { Check } from "@untitledui/icons"
import type { ComponentType, CSSProperties, Key, ReactNode } from "react"
import {
  createContext,
  useCallback,
  useContext,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import type { FocusManager } from "react-aria"
import { FocusScope, mergeProps, useFocusManager, useKeyboard } from "react-aria"
import type { PopoverProps as AriaPopoverProps } from "react-aria-components"
import {
  Dialog as AriaDialog,
  Popover as AriaPopover,
  Button,
  DialogTrigger,
  Disclosure,
  DisclosurePanel,
  DisclosureStateContext,
  ToggleButton,
} from "react-aria-components"
import { BodySwap, DrilldownPanel, SlideSwap } from "./DrilldownSwap"
import { isTextEntryTarget, useMenuTypeSelect } from "./use-type-select"

export type IconComponent = ComponentType<{ className?: string }>

export interface DrilldownApi {
  /** Returns to the previous step, or to the root menu from a drilldown's first step. */
  back: () => void
  close: () => void
  /** Opens `view` as the next step of this drilldown; its back arrow returns here. */
  push: (view: DrilldownView) => void
}

export interface DrilldownView {
  header: (api: DrilldownApi) => ReactNode
  body: (api: DrilldownApi) => ReactNode
}

/** What the root menu is handed — the same way a drilled-in view gets its {@link DrilldownApi}. */
export interface RootMenuApi {
  close: () => void
}

export interface DrilldownMenuProps {
  ariaLabel: string
  trigger: ReactNode
  header: ReactNode
  /**
   * The root menu. Closing is always explicit: a row that should end the menu is wired with
   * the `close` handed here, exactly as a drilled-in view wires the one in its api.
   */
  children: (menu: RootMenuApi) => ReactNode
  className?: string
  /**
   * Where the menu opens relative to its trigger. The default suits the card-stack menus,
   * which hang off a header pill with the whole page to their right; a menu triggered from
   * inside a panel that has no room beside it wants a vertical placement instead.
   */
  placement?: AriaPopoverProps["placement"]
  onOpenChange?: (isOpen: boolean) => void
}

interface ClassNameProps {
  className?: string
}

interface ContentProps extends ClassNameProps {
  children: ReactNode
}

type ComposedNode = Exclude<ReactNode, string>
type ChildFace =
  | { children: string; typeaheadText?: never }
  | { children: ComposedNode; typeaheadText: string }

const MENU_NAV_KEYS = new Set(["ArrowDown", "ArrowUp", "Home", "End"])
const DISCLOSURE_PANEL_CLASS =
  "flex h-(--disclosure-panel-height) flex-col overflow-clip motion-safe:transition-[height] motion-safe:duration-200 motion-safe:ease-out"

const isExposedToMenuNavigation = (element: Element) =>
  element.closest('[aria-hidden="true"]') === null

interface MenuNavigationEvent {
  readonly key: string
  readonly target: EventTarget | null
  preventDefault: () => void
}

function moveMenuFocus(focusManager: FocusManager, event: MenuNavigationEvent): boolean {
  if (!MENU_NAV_KEYS.has(event.key) || isTextEntryTarget(event.target)) return false

  event.preventDefault()
  const options = { wrap: true, tabbable: true, accept: isExposedToMenuNavigation }
  if (event.key === "ArrowDown") focusManager.focusNext(options)
  else if (event.key === "ArrowUp") focusManager.focusPrevious(options)
  else if (event.key === "Home") focusManager.focusFirst(options)
  else focusManager.focusLast(options)
  return true
}

const ResetTypeSelectContext = createContext<() => void>(() => {})

/**
 * The menu's keyboard behavior, in one place because it belongs to the menu rather than to any
 * one panel: Escape backs out of a drilldown, the arrows and Home/End move between rows, and
 * typing jumps to a row by its label. It reaches whichever surface is showing without knowing
 * that surfaces exist — both movement and typeahead skip anything `aria-hidden`, and the
 * surface sliding out is exactly that. So a drilled-in view is navigable without a container
 * of its own; its rows only have to be rows.
 */
function MenuKeyboardScope({
  isDrilledIn,
  onBack,
  children,
}: {
  isDrilledIn: boolean
  onBack: () => void
  children: ReactNode
}) {
  const menuRef = useRef<HTMLDivElement | null>(null)
  const { typeSelectProps, resetTypeSelect } = useMenuTypeSelect(menuRef)
  const focusManager = useFocusManager()
  const { keyboardProps } = useKeyboard({
    onKeyDown(event) {
      if (event.key === "Escape" && isDrilledIn) {
        event.preventDefault()
        onBack()
        return
      }
      if (focusManager && moveMenuFocus(focusManager, event)) return
      event.continuePropagation()
    },
  })

  return (
    <ResetTypeSelectContext.Provider value={resetTypeSelect}>
      <div
        {...mergeProps(typeSelectProps, keyboardProps)}
        ref={menuRef}
        className="flex min-h-0 flex-col"
      >
        {children}
      </div>
    </ResetTypeSelectContext.Provider>
  )
}

interface DrilldownRegistration {
  readonly id: string
  element: HTMLButtonElement | null
  view: DrilldownView
  visit: number
}

interface MenuActions {
  openDrilldown: (id: string) => void
  registerDrilldown: (registration: DrilldownRegistration) => () => void
  notifyDrilldownViewChanged: (id: string) => void
  resetTypeSelect: () => void
}

const MenuActionsContext = createContext<MenuActions | null>(null)

function useMenuActions(): MenuActions {
  const actions = useContext(MenuActionsContext)
  if (actions === null) throw new Error("Menu rows must be rendered inside a MenuRoot")
  return actions
}

interface MenuRootHost {
  openDrilldown: (id: string) => void
  registerDrilldown: (registration: DrilldownRegistration) => () => void
  notifyDrilldownViewChanged: (id: string) => void
  onMount: (rootPanel: HTMLDivElement | null) => void
}

const MenuRootHostContext = createContext<MenuRootHost | null>(null)

function MenuRootHostProvider({
  openDrilldown,
  registerDrilldown,
  notifyDrilldownViewChanged,
  onMount,
  children,
}: MenuRootHost & { children: ReactNode }) {
  const host = useMemo(
    () => ({ openDrilldown, registerDrilldown, notifyDrilldownViewChanged, onMount }),
    [notifyDrilldownViewChanged, onMount, openDrilldown, registerDrilldown],
  )
  return <MenuRootHostContext.Provider value={host}>{children}</MenuRootHostContext.Provider>
}

export interface MenuRootProps extends ContentProps {}

export function MenuRoot({ children, className }: MenuRootProps) {
  const host = useContext(MenuRootHostContext)
  if (host === null) throw new Error("MenuRoot must be rendered inside a DrilldownMenu")

  const resetTypeSelect = useContext(ResetTypeSelectContext)
  const setRootPanel = useCallback(
    (rootPanel: HTMLDivElement | null) => {
      host.onMount(rootPanel)
    },
    [host.onMount],
  )
  const actions = useMemo(
    () => ({
      openDrilldown: host.openDrilldown,
      registerDrilldown: host.registerDrilldown,
      notifyDrilldownViewChanged: host.notifyDrilldownViewChanged,
      resetTypeSelect,
    }),
    [host.notifyDrilldownViewChanged, host.openDrilldown, host.registerDrilldown, resetTypeSelect],
  )

  return (
    <MenuActionsContext.Provider value={actions}>
      {/* No horizontal padding: rows run edge to edge so the whole width of one is clickable.
          Whatever needs an inset (a section's pill, a row's own label) pads itself. */}
      <div
        ref={setRootPanel}
        data-test="menu root"
        className={cx("flex flex-col gap-2 pb-3", className)}
      >
        {children}
      </div>
    </MenuActionsContext.Provider>
  )
}

export const DrilldownMenu = tracked(function DrilldownMenu({
  ariaLabel,
  trigger,
  header,
  children,
  className,
  placement = "right top",
  onOpenChange,
}: DrilldownMenuProps) {
  const [activeDrilldownId, setActiveDrilldownId] = useState<string | null>(null)
  // The steps pushed on top of the drilled-into row's own view, deepest last.
  const [pushedViews, setPushedViews] = useState<ReadonlyArray<DrilldownView>>([])
  const returningFocusId = useRef<string | null>(null)
  const nextDrilldownVisit = useRef(0)
  const rootPanelRef = useRef<HTMLDivElement | null>(null)
  const registrations = useRef(new Map<string, DrilldownRegistration>())
  const activeDrilldownIdRef = useRef<string | null>(null)
  const [, refreshActiveView] = useState(0)
  const drilldownTitleIdPrefix = useId()
  activeDrilldownIdRef.current = activeDrilldownId

  const mountRootPanel = useCallback((rootPanel: HTMLDivElement | null) => {
    rootPanelRef.current = rootPanel
  }, [])

  const registerDrilldown = useCallback((registration: DrilldownRegistration) => {
    const existing = registrations.current.get(registration.id)
    if (existing !== undefined && existing !== registration) {
      throw new Error(`Duplicate drilldown control id ${registration.id}`)
    }
    registrations.current.set(registration.id, registration)
    return () => {
      if (registrations.current.get(registration.id) === registration) {
        registrations.current.delete(registration.id)
      }
    }
  }, [])

  const openDrilldown = useCallback((id: string) => {
    const registration = registrations.current.get(id)
    if (registration === undefined) throw new Error(`Drilldown control ${id} is not registered`)
    if (registration.element === null) {
      throw new Error(`Drilldown control ${id} is not mounted`)
    }
    nextDrilldownVisit.current += 1
    registration.visit = nextDrilldownVisit.current
    setPushedViews([])
    setActiveDrilldownId(id)
  }, [])

  const push = useCallback((view: DrilldownView) => {
    setPushedViews((views) => [...views, view])
  }, [])

  const notifyDrilldownViewChanged = useCallback((id: string) => {
    if (activeDrilldownIdRef.current === id) {
      refreshActiveView((revision) => revision + 1)
    }
  }, [])

  const back = useCallback(() => {
    if (activeDrilldownId === null) throw new Error("Cannot go back from the root menu")
    if (pushedViews.length > 0) {
      setPushedViews((views) => views.slice(0, -1))
      return
    }
    returningFocusId.current = activeDrilldownId
    setActiveDrilldownId(null)
  }, [activeDrilldownId, pushedViews.length])

  useLayoutEffect(() => {
    if (activeDrilldownId !== null || returningFocusId.current === null) return
    const rootPanel = rootPanelRef.current
    if (rootPanel === null) throw new Error("Menu root is not mounted")
    const registration = registrations.current.get(returningFocusId.current)
    if (registration === undefined || registration.element === null) {
      throw new Error(`Menu control ${returningFocusId.current} is not registered`)
    }
    if (!rootPanel.contains(registration.element)) {
      throw new Error(`Menu control ${returningFocusId.current} is outside the menu root`)
    }
    registration.element.focus({ preventScroll: true })
    returningFocusId.current = null
  }, [activeDrilldownId])

  // Closing the popover unmounts its rows without clearing the drilled-into id (the exit
  // animation still needs the view), so an id whose registration is gone means the menu is on
  // its way out — it opens at the root next time. Reading the registration keeps the exiting
  // view painted for as long as it exists.
  const activeRegistration =
    activeDrilldownId === null ? undefined : registrations.current.get(activeDrilldownId)
  // Each step is its own key, so pushing and popping slide like drilling in and out do.
  const depth = pushedViews.length
  const activeKey: Key | null =
    activeRegistration === undefined ? null : `${activeRegistration.visit}:${depth}`
  const activeView =
    activeRegistration === undefined ? null : (pushedViews[depth - 1] ?? activeRegistration.view)
  const drilldownTitleId =
    activeRegistration === undefined ? null : `${drilldownTitleIdPrefix}-${activeKey}`

  return (
    <DialogTrigger
      onOpenChange={(isOpen) => {
        if (isOpen) {
          returningFocusId.current = null
          setPushedViews([])
          setActiveDrilldownId(null)
        }
        onOpenChange?.(isOpen)
      }}
    >
      {trigger}
      <Dropdown.Popover
        placement={placement}
        arrow
        scrim
        holdsTop
        className={cx("shadow-menu w-80 will-change-transform", className)}
      >
        <AriaDialog
          aria-label={ariaLabel}
          className="flex max-h-[inherit] flex-col overflow-hidden rounded-[inherit] outline-hidden"
        >
          {({ close }) => {
            const api = { back, close, push }
            return (
              <FocusScope autoFocus>
                <MenuKeyboardScope isDrilledIn={activeDrilldownId !== null} onBack={back}>
                  <div className="relative shrink-0 overflow-clip">
                    <SlideSwap axis="y" activeKey={activeKey} root={header}>
                      {activeView === null || drilldownTitleId === null ? null : (
                        <DrilldownTitleScope titleId={drilldownTitleId}>
                          {activeView.header(api)}
                        </DrilldownTitleScope>
                      )}
                    </SlideSwap>
                  </div>

                  <BodySwap
                    activeKey={activeKey}
                    root={
                      <MenuRootHostProvider
                        onMount={mountRootPanel}
                        openDrilldown={openDrilldown}
                        registerDrilldown={registerDrilldown}
                        notifyDrilldownViewChanged={notifyDrilldownViewChanged}
                      >
                        <RootMenuContent close={close}>{children}</RootMenuContent>
                      </MenuRootHostProvider>
                    }
                  >
                    {activeView === null || drilldownTitleId === null ? null : (
                      <DrilldownPanel labelledBy={drilldownTitleId}>
                        {activeView.body(api)}
                      </DrilldownPanel>
                    )}
                  </BodySwap>
                </MenuKeyboardScope>
              </FocusScope>
            )
          }}
        </AriaDialog>
      </Dropdown.Popover>
    </DialogTrigger>
  )
})

/**
 * Invokes the root's render function once per change of the function itself — the menu host
 * re-rendering — not once per internal re-render of the drilldown. The rows it returns
 * register themselves with the menu; handing DrilldownRow a fresh header/body closure on
 * every internal state change would re-register in a loop.
 */
function RootMenuContent({
  close,
  children,
}: {
  close: () => void
  children: (menu: RootMenuApi) => ReactNode
}) {
  // RAC's Dialog hands its render prop a fresh `close` per render; route it through a ref so
  // an unstable identity can't defeat the memo below.
  const closeRef = useRef(close)
  closeRef.current = close
  const stableClose = useCallback(() => closeRef.current(), [])
  return useMemo(() => children({ close: stableClose }), [children, stableClose])
}

export interface MenuHeaderProps extends ContentProps {}

export function MenuHeader({ children, className }: MenuHeaderProps) {
  // Matches DrilldownHeader's padding: the two swap in the same bar, and a height change
  // between them makes the popover jump mid-animation.
  return (
    <div data-test="menu header" className={cx("px-5 py-4 text-left", className)}>
      {children}
    </div>
  )
}

export interface MenuTitleProps extends ContentProps {}

export function MenuTitle({ children, className }: MenuTitleProps) {
  // leading-none like DrilldownTitle: the two swap in the same bar, so the root title's line
  // box has to be the same height as a view title's.
  return <h3 className={cx("m-0 menu-header leading-none", className)}>{children}</h3>
}

export interface RowsProps extends ContentProps {}

export function Rows({ children, className }: RowsProps) {
  return <div className={cx("flex flex-col pt-1.5 pb-0.5", className)}>{children}</div>
}

export interface SectionsProps extends ContentProps {}

export function Sections({ children, className }: SectionsProps) {
  return <div className={cx("flex flex-col gap-2", className)}>{children}</div>
}

export interface SectionProps extends ClassNameProps {
  typeaheadText: string
  heading: ReactNode
  children: ReactNode
  isExpanded: boolean
  onExpandedChange: (isExpanded: boolean) => void
}

export function Section({
  typeaheadText,
  heading,
  children,
  isExpanded,
  onExpandedChange,
  className,
}: SectionProps) {
  const { resetTypeSelect } = useMenuActions()

  return (
    <Disclosure
      isExpanded={isExpanded}
      onExpandedChange={(next) => {
        resetTypeSelect()
        onExpandedChange(next)
      }}
    >
      {/* The pill's own inset — its panel's rows go edge to edge, so the menu root has none. */}
      <h3 className="m-0 px-3">
        <Button
          slot="trigger"
          data-test="section heading"
          data-menu-control
          data-menu-text-value={typeaheadText}
          className={cx(
            "group block w-full cursor-pointer rounded-full text-left outline-hidden data-focus-visible:outline-2 data-focus-visible:outline-offset-2 data-focus-visible:outline-focus-ring",
            className,
          )}
        >
          {heading}
        </Button>
      </h3>

      <DisclosurePanel className={DISCLOSURE_PANEL_CLASS}>{children}</DisclosurePanel>
    </Disclosure>
  )
}

export interface SectionHeadingProps extends ContentProps {}

export function SectionHeading({ children, className }: SectionHeadingProps) {
  // gap-2, where a row's RowContent uses gap-3: the pill starts 4px further left than a row
  // (px-3 + px-4, against a row's px-7 inset) but its icon box is 8px wider, so an equal gap
  // would set the section's title 4px right of the labels underneath it. The narrower gap
  // spends that 4px and lands both texts on the same x.
  return (
    <span
      className={cx(
        "flex items-center gap-2 rounded-full bg-gray-100 px-4 py-2.5 transition-colors group-data-hovered:bg-gray-200",
        className,
      )}
    >
      {children}
    </span>
  )
}

export interface SectionIconProps extends ClassNameProps {
  icon: IconComponent
}

export function SectionIcon({ icon: Icon, className }: SectionIconProps) {
  // A fixed box with the artwork centred in it, so every pill is the same height and every
  // title starts at the same x whatever one icon's drawing does. `className` sizes the icon
  // inside the box, never the box. And a flex box rather than a bare span: an inline one puts
  // the icon on a text baseline, which reserves the font's descender space beneath it — dead
  // height at the bottom of the pill.
  return (
    <span className="flex size-6 shrink-0 items-center justify-center">
      <Icon className={cx("size-6 shrink-0", className)} />
    </span>
  )
}

export interface SectionTitleProps extends ContentProps {}

export function SectionTitle({ children, className }: SectionTitleProps) {
  return <span className={cx("grow text-lg font-extrabold", className)}>{children}</span>
}

export interface RowContentProps extends ContentProps {}

export function RowContent({ children, className }: RowContentProps) {
  // gap-3, not gap-4: at a 16px icon the wider gap reads as two columns rather than one
  // labelled row.
  return <span className={cx("flex min-w-0 grow items-center gap-3", className)}>{children}</span>
}

export interface RowIconProps extends ClassNameProps {
  icon: IconComponent
  /**
   * Sizes the drawing inside the box instead of letting it fill. For a row set smaller than
   * its neighbours: the box stays the column's width, so every label still starts at the same
   * x, and only the artwork comes down with the type.
   */
  artClassName?: string
}

/**
 * One line weight down the icon column, whatever scale each drawing's own viewBox puts it at.
 * A stroke inside a viewBox is scaled with the artwork, so trimming a box or resizing a column
 * lands the line on a fractional width — 1.33px paints as a dark pixel beside a pale one, which
 * reads as a blurry double outline rather than a clean edge. `non-scaling-stroke` takes the
 * width out of that transform, so 1px is 1px and sits on the pixel grid at any DPR.
 *
 * Both halves have to reach the shapes themselves: a `stroke-width` attribute on a path beats
 * an inherited value, and `vector-effect` is not an inherited property at all.
 */
const CRISP_ICON_STROKE = "[&_*]:[vector-effect:non-scaling-stroke] [&_*]:stroke-1"

/** A disabled row sets this, and its icon and label make themselves grey. */
const DisabledFace = createContext(false)

export function RowIcon({ icon: Icon, className, artClassName }: RowIconProps) {
  const isDisabledFace = useContext(DisabledFace)

  // Like SectionIcon, a fixed box with the artwork centred in it — here `className` styles and
  // sizes the BOX (the icon fills it), so a menu that wants a roomier icon column moves the
  // whole column rather than letting one icon outgrow its neighbours' alignment.
  return (
    <span
      className={cx(
        "group-data-focus-visible:text-link-blue group-data-hovered:text-link-blue ml-1 flex size-4 shrink-0 items-center justify-center text-gray-500",
        isDisabledFace &&
          "text-gray-400 group-data-focus-visible:text-gray-400 group-data-hovered:text-gray-400",
        className,
      )}
    >
      <Icon className={cx(artClassName ?? "size-full", CRISP_ICON_STROKE)} />
    </span>
  )
}

/**
 * The kinds of thing a menu row can name, which is what decides how its label is set. Every
 * size here is a Tailwind step; the 15px `action` these grew out of was the legacy Ember menus'
 * own size, carried over because the new menus replaced them in place and had to sit flush.
 *
 * - `action` — a row that does something to the thing the menu belongs to ("Erase Lesson").
 *   Set like a command: heavy and black, so a list of them scans as a list of verbs.
 * - `content` — a row that names something to insert ("Heading 1", "Picture"). Set like prose:
 *   regular weight, off-black, because the reader is picking a noun, not firing a command, and
 *   the row's icon is already carrying the emphasis.
 * - `aside` — a row that leaves this menu's subject behind for a setting kept somewhere else
 *   ("Change the default template for this class"). Set as the footnote it is, well under the
 *   list it hangs off. Pair it with a `RowIcon` whose `artClassName` brings the drawing down
 *   to match.
 *
 * All are first-class. None is a default the others have to override — a label that reaches
 * for `className` to restyle itself is the smell this exists to remove.
 */
const ROW_LABEL_KINDS = {
  action: "text-base font-semibold text-black",
  aside: "text-xs font-semibold text-black",
  content: "text-base font-normal text-gray-700",
  // A disabled row. This also cancels the hover and focus color, because a press only shows the
  // reason.
  disabled:
    "text-base font-semibold text-gray-400 group-data-focus-visible:text-gray-400 group-data-hovered:text-gray-400",
} as const

export type RowLabelKind = keyof typeof ROW_LABEL_KINDS

export interface RowLabelProps extends ContentProps {
  kind?: RowLabelKind
}

export function RowLabel({ children, className, kind = "action" }: RowLabelProps) {
  const isDisabledFace = useContext(DisabledFace)

  return (
    <span
      data-test="menu row label"
      className={cx(
        "group-data-focus-visible:text-link-blue group-data-hovered:text-link-blue leading-none",
        ROW_LABEL_KINDS[isDisabledFace ? "disabled" : kind],
        className,
      )}
    >
      {children}
    </span>
  )
}

/**
 * A row is as wide as the menu, and all of that width takes the press — no dead strip beside
 * a label. So its inset is padding, never margin, and travels as `--menu-row-inset`: a
 * variable, so the container that knows the inset can set it (see
 * {@link FLAT_MENU_ROWS_CLASS}) without racing this utility for specificity. The default is a
 * row nested under a section's pill.
 */
function rowClassName(className?: string): string {
  return cx(
    // data-focus-visible:outline-solid is load-bearing: `outline-hidden` sets --tw-outline-style
    // to none, which the focus ring's width inherits, so without it the ring resolves to 2px of
    // the right colour and never paints. A row that recolours its own text has nothing else to
    // show focus with.
    "data-focus-visible:outline-focus-ring data-focus-visible:text-link-blue data-hovered:text-link-blue group flex cursor-pointer items-center self-stretch px-[var(--menu-row-inset,1.75rem)] py-2 text-left leading-none text-gray-700 outline-hidden data-disabled:cursor-default data-focus-visible:outline-solid data-focus-visible:outline-2 data-focus-visible:-outline-offset-2",
    className,
  )
}

function typeaheadTextFor(face: ChildFace): string {
  if (typeof face.children === "string") return face.children
  if (face.typeaheadText === undefined) {
    throw new Error("Composed menu row faces require typeaheadText")
  }
  return face.typeaheadText
}

function rowFace(children: ReactNode): ReactNode {
  return typeof children === "string" ? (
    <RowContent>
      <RowLabel>{children}</RowLabel>
    </RowContent>
  ) : (
    children
  )
}

/**
 * A disabled row is grey and does not do its action. With a `disabledReason` it stays pressable
 * and shows that reason in a popover. A fully disabled button accepts no focus and no press, so
 * the user could not read the reason.
 */
interface Disabled {
  isDisabled?: boolean
  /** Why, in the user's terms: "Class has no lesson today, so nothing to erase". */
  disabledReason?: string
}

export type RowProps = ClassNameProps &
  ChildFace &
  Disabled & {
    onAction?: () => void
    /** For the custom properties a styled row reads — a row that paints itself in a colour
     * only its caller knows has nowhere else to hand that colour in. */
    style?: CSSProperties
  }

/** A grey row. A press shows the reason. */
function DisabledRow({
  reason,
  className,
  children,
  typeaheadText,
}: {
  reason: string
  className?: string
  children: ReactNode
  typeaheadText: string
}) {
  return (
    <DialogTrigger>
      <Button
        data-test="menu row"
        data-menu-control
        data-menu-text-value={typeaheadText}
        className={rowClassName(className)}
      >
        <DisabledFace.Provider value={true}>{children}</DisabledFace.Provider>
      </Button>
      {/* Narrower than the menu, so it belongs to one row. */}
      <AriaPopover placement="bottom" className="shadow-menu w-64 rounded-lg bg-white p-3">
        <AriaDialog
          data-test="disabled reason"
          className="text-sm leading-snug text-gray-700 outline-hidden"
        >
          {reason}
        </AriaDialog>
      </AriaPopover>
    </DialogTrigger>
  )
}

/**
 * A row does nothing but its `onAction`. A row that should end the menu is wired with the
 * `close` its menu was handed — the root's from {@link RootMenuApi}, a view's from its
 * {@link DrilldownApi} — closing first, so a modal the action opens keeps its focus.
 */
export function Row(props: RowProps) {
  const { className, style, onAction, isDisabled = false, disabledReason } = props

  if (isDisabled && disabledReason) {
    return (
      <DisabledRow
        reason={disabledReason}
        className={className}
        typeaheadText={typeaheadTextFor(props)}
      >
        {rowFace(props.children)}
      </DisabledRow>
    )
  }

  return (
    <Button
      data-test="menu row"
      data-menu-control
      data-menu-text-value={typeaheadTextFor(props)}
      isDisabled={isDisabled}
      className={rowClassName(className)}
      style={style}
      onPress={() => onAction?.()}
    >
      <DisabledFace.Provider value={isDisabled}>{rowFace(props.children)}</DisabledFace.Provider>
    </Button>
  )
}

export type DrilldownRowProps = ClassNameProps &
  ChildFace &
  Disabled & {
    header: (api: DrilldownApi) => ReactNode
    body: (api: DrilldownApi) => ReactNode
  }

export function DrilldownRow(props: DrilldownRowProps) {
  const { className, header, body, isDisabled = false, disabledReason } = props
  const { openDrilldown, registerDrilldown, notifyDrilldownViewChanged } = useMenuActions()
  const controlId = useId()
  const registration = useRef<DrilldownRegistration>({
    id: controlId,
    element: null,
    view: { header, body },
    visit: 0,
  })

  useLayoutEffect(() => registerDrilldown(registration.current), [registerDrilldown])

  useLayoutEffect(() => {
    registration.current.view = { header, body }
    notifyDrilldownViewChanged(controlId)
  }, [body, controlId, header, notifyDrilldownViewChanged])

  const setElement = useCallback((element: HTMLButtonElement | null) => {
    registration.current.element = element
  }, [])

  // This is below the hooks. An early return changes how many hooks run. The view stays
  // registered but never opens, because only `openDrilldown` uses a registration.
  if (isDisabled && disabledReason) {
    return (
      <DisabledRow
        reason={disabledReason}
        className={className}
        typeaheadText={typeaheadTextFor(props)}
      >
        {rowFace(props.children)}
      </DisabledRow>
    )
  }

  return (
    <Button
      ref={setElement}
      data-test="menu row"
      data-menu-control
      data-menu-id={controlId}
      data-menu-text-value={typeaheadTextFor(props)}
      isDisabled={isDisabled}
      className={rowClassName(className)}
      onPress={() => openDrilldown(controlId)}
    >
      <DisabledFace.Provider value={isDisabled}>{rowFace(props.children)}</DisabledFace.Provider>
      <span className="sr-only">, opens more options</span>
    </Button>
  )
}

export interface DisclosureRowProps extends ClassNameProps {
  typeaheadText: string
  face: ReactNode
  children: ReactNode
}

export function DisclosureRow({ typeaheadText, face, children, className }: DisclosureRowProps) {
  const { resetTypeSelect } = useMenuActions()

  return (
    <Disclosure className="w-full" onExpandedChange={resetTypeSelect}>
      <Button
        slot="trigger"
        data-test="menu row"
        data-menu-control
        data-menu-text-value={typeaheadText}
        className={rowClassName(cx("w-full", className))}
      >
        {face}
      </Button>

      <DisclosurePanel className={DISCLOSURE_PANEL_CLASS}>{children}</DisclosurePanel>
    </Disclosure>
  )
}

const ToggleRowStateContext = createContext<boolean | null>(null)

export type ToggleRowProps = ClassNameProps &
  ChildFace & {
    isSelected: boolean
    onChange: (isSelected: boolean) => void
  }

export function ToggleRow(props: ToggleRowProps) {
  const { className, isSelected, onChange } = props

  return (
    <ToggleButton
      data-test="menu row"
      data-menu-control
      data-menu-text-value={typeaheadTextFor(props)}
      isSelected={isSelected}
      onChange={onChange}
      className={rowClassName(className)}
    >
      <ToggleRowStateContext.Provider value={isSelected}>
        {rowFace(props.children)}
      </ToggleRowStateContext.Provider>
    </ToggleButton>
  )
}

export function ToggleCheck({ className }: ClassNameProps) {
  const isSelected = useContext(ToggleRowStateContext)
  if (isSelected === null) throw new Error("ToggleCheck must be rendered inside a ToggleRow")

  return (
    <span
      hidden={!isSelected}
      className={cx("text-link-blue ml-auto flex size-4 shrink-0 *:size-4", className)}
    >
      <Check />
    </span>
  )
}

export function DisclosureChevron({ className }: ClassNameProps) {
  const state = useContext(DisclosureStateContext)
  if (state === null) {
    throw new Error("DisclosureChevron must be rendered inside a Section or DisclosureRow")
  }
  return <ExpandChevron isExpanded={state.isExpanded} className={cx("ml-auto", className)} />
}

/**
 * The chevron on a section's pill. A pill is already a filled shape, so a bare chevron on it
 * reads as part of the decoration; on a disc of its own it reads as the control that opens the
 * section. The disc holds still and the chevron turns inside it. Rows keep the bare
 * {@link DisclosureChevron} — a row has no fill for a chevron to get lost in.
 */
export function SectionChevron({ className }: ClassNameProps) {
  const state = useContext(DisclosureStateContext)
  if (state === null) throw new Error("SectionChevron must be rendered inside a Section")

  return (
    <span
      className={cx(
        "ml-auto flex size-6 shrink-0 items-center justify-center rounded-full bg-white ring-1 ring-gray-200 ring-inset",
        className,
      )}
    >
      <ExpandChevron isExpanded={state.isExpanded} className="text-gray-700" />
    </span>
  )
}

interface DrilldownTitleContextValue {
  id: string
  register: () => () => void
}

const DrilldownTitleContext = createContext<DrilldownTitleContextValue | null>(null)

function DrilldownTitleScope({ titleId, children }: { titleId: string; children: ReactNode }) {
  const titleCount = useRef(0)
  const register = useCallback(() => {
    titleCount.current += 1
    return () => {
      titleCount.current -= 1
    }
  }, [])
  const context = useMemo(() => ({ id: titleId, register }), [register, titleId])

  useLayoutEffect(() => {
    if (titleCount.current !== 1) {
      throw new Error("A drilldown header must contain exactly one DrilldownTitle")
    }
  })

  return <DrilldownTitleContext.Provider value={context}>{children}</DrilldownTitleContext.Provider>
}

export interface HeaderWithBackArrowProps extends ContentProps {
  /** Where the arrow returns to, named for its screen-reader label ("Back to Lesson Menu"). */
  backTo: string
  onBack: () => void
}

/**
 * A drilled-in view's header: back arrow, then title. The header for every drilled-in view —
 * `backTo` names the root menu in a one-step drilldown, and the previous step in a pushed flow.
 */
export function HeaderWithBackArrow({
  backTo,
  onBack,
  children,
  className,
}: HeaderWithBackArrowProps) {
  return (
    <DrilldownHeader className={className}>
      <DrilldownBack onPress={onBack}>{backTo}</DrilldownBack>
      <DrilldownTitle>{children}</DrilldownTitle>
    </DrilldownHeader>
  )
}

export interface DrilldownHeaderProps extends ContentProps {}

export function DrilldownHeader({ children, className }: DrilldownHeaderProps) {
  return (
    <h3
      data-test="view header"
      className={cx(
        "relative m-0 flex items-center border-b border-border-secondary px-5 py-4 text-left",
        className,
      )}
    >
      {children}
    </h3>
  )
}

export interface DrilldownBackProps extends ContentProps {
  onPress: () => void
}

export function DrilldownBack({ onPress, children, className }: DrilldownBackProps) {
  return (
    <Button
      data-test="view back"
      onPress={onPress}
      className={({ isHovered, isFocusVisible }) =>
        cx(
          "absolute inset-0 flex w-full cursor-pointer items-center px-5 text-left transition-colors",
          isFocusVisible ? "outline-2 -outline-offset-2 outline-focus-ring" : "outline-hidden",
          isHovered && "text-link-blue",
          className,
        )
      }
    >
      <IconArrowLeft className="size-3.75 shrink-0" />
      <span className="sr-only">Back to {children}</span>
    </Button>
  )
}

export interface DrilldownTitleProps extends ContentProps {}

export function DrilldownTitle({ children, className }: DrilldownTitleProps) {
  const context = useContext(DrilldownTitleContext)
  if (context === null) throw new Error("DrilldownTitle must be rendered in a drilldown header")

  useLayoutEffect(() => context.register(), [context])

  return (
    <span
      id={context.id}
      className={cx(
        "pointer-events-none relative ml-[1.3125rem] menu-header leading-none",
        className,
      )}
    >
      {children}
    </span>
  )
}
